// frontend/src/components/shared/modals/SemesterSelectionModal.tsx
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

type SemesterType = 'bookmarks' | 'other' | 'fall' | 'winter' | 'spring' | 'summer';

interface SemesterOption {
  id: string;
  label: string;
  type: SemesterType;
}

export interface SemesterSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (semesterId: string) => void;
  excludeSemesters?: string[];
}

const SemesterSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  excludeSemesters = [],
}: SemesterSelectionModalProps) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  useEscapeKey(handleClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleSelect = (semesterId: string) => {
    onSelect(semesterId);
    handleClose();
  };

  if (!isOpen) return null;

  const semesters: SemesterOption[] = [
    { id: 'bookmarks', label: 'Bookmarked Courses', type: 'bookmarks' },
    { id: '000009', label: 'Other Credits', type: 'other' },
    { id: '202308', label: 'Fall 2023', type: 'fall' },
    { id: '202312', label: 'Winter 2024', type: 'winter' },
    { id: '202401', label: 'Spring 2024', type: 'spring' },
    { id: '202405', label: 'Summer 2024', type: 'summer' },
    { id: '202408', label: 'Fall 2024', type: 'fall' },
    { id: '202412', label: 'Winter 2025', type: 'winter' },
    { id: '202501', label: 'Spring 2025', type: 'spring' },
    { id: '202505', label: 'Summer 2025', type: 'summer' },
    { id: '202508', label: 'Fall 2025', type: 'fall' },
    { id: '202512', label: 'Winter 2026', type: 'winter' },
    { id: '202601', label: 'Spring 2026', type: 'spring' },
    { id: '202605', label: 'Summer 2026', type: 'summer' },
    { id: '202608', label: 'Fall 2026', type: 'fall' },
    { id: '202612', label: 'Winter 2027', type: 'winter' },
    { id: '202701', label: 'Spring 2027', type: 'spring' },
    { id: '202705', label: 'Summer 2027', type: 'summer' },
    { id: '202708', label: 'Fall 2027', type: 'fall' },
    { id: '202712', label: 'Winter 2028', type: 'winter' },
    { id: '202801', label: 'Spring 2028', type: 'spring' },
    { id: '202805', label: 'Summer 2028', type: 'summer' },
    { id: '202808', label: 'Fall 2028', type: 'fall' },
    { id: '202812', label: 'Winter 2029', type: 'winter' },
    { id: '202901', label: 'Spring 2029', type: 'spring' },
    { id: '202905', label: 'Summer 2029', type: 'summer' },
    { id: '202908', label: 'Fall 2029', type: 'fall' },
    { id: '202912', label: 'Winter 2030', type: 'winter' },
    { id: '203001', label: 'Spring 2030', type: 'spring' },
    { id: '203005', label: 'Summer 2030', type: 'summer' },
    { id: '203008', label: 'Fall 2030', type: 'fall' },
  ];

  const availableSemesters = semesters.filter(
    (semester) => !excludeSemesters.includes(semester.id)
  );

  const getSeasonStyle = (type: SemesterType): string => {
    switch (type) {
      case 'bookmarks':
        return 'bg-stone-100 border-stone-200 hover:bg-stone-150 hover:border-stone-300';
      case 'other':
        return 'bg-indigo-400/10 border-line-soft hover:bg-indigo-400/15';
      case 'winter':
        return 'bg-blue-400/10 border-line-soft hover:bg-blue-400/15';
      case 'spring':
        return 'bg-green-500/10 border-line-soft hover:bg-green-500/15';
      case 'summer':
        return 'bg-yellow-400/10 border-line-soft hover:bg-yellow-400/15';
      case 'fall':
        return 'bg-orange-400/10 border-line-soft hover:bg-orange-400/15';
      default:
        return 'bg-surface-sunken/70 border-line-soft hover:bg-surface-sunken';
    }
  };

  const isSpecialType = (type: SemesterType): boolean =>
    type === 'bookmarks' || type === 'other';

  return (
    <div
      className={`fixed inset-0 bg-black transition-opacity duration-200 flex items-center justify-center z-50 ${
        isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-hidden transition-all duration-200 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Move to...</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-surface-sunken rounded-control transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 gap-2">
            {availableSemesters.map((semester, index) => {
              const prevSemester = index > 0 ? availableSemesters[index - 1] : null;
              const showSeparator =
                !!prevSemester &&
                ((isSpecialType(prevSemester.type) && !isSpecialType(semester.type)) ||
                  (!isSpecialType(prevSemester.type) && isSpecialType(semester.type)));

              return (
                <React.Fragment key={semester.id}>
                  {showSeparator && <div className="border-t border-gray-200 my-2" />}
                  <button
                    onClick={() => handleSelect(semester.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all duration-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent active:scale-[0.98] ${getSeasonStyle(semester.type)}`}
                  >
                    <span className="font-semibold text-gray-800">{semester.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemesterSelectionModal;
